/**
 * Retrouve une option commercial à partir du préremplissage Dolibarr.
 * Gère les ids numériques (plan de com), les slugs (chiffrage) et les alias email.
 *
 * @param {Array<{value?: string|number, id?: string|number, label?: string, nom?: string, email?: string}>} options
 * @param {{
 *   commercial_id?: string|number|null,
 *   commercial_nom?: string|null,
 *   commercial_email?: string|null,
 *   commercial_emails?: string[]|null,
 * }} prefill
 * @returns {{value: string, label: string, email?: string}|null}
 */
export function matchCommercialOption(options, prefill = {}) {
  const list = Array.isArray(options) ? options : [];
  if (!list.length) return null;

  const idStr = prefill.commercial_id != null && prefill.commercial_id !== ''
    ? String(prefill.commercial_id)
    : '';
  const emails = new Set(
    [
      ...(Array.isArray(prefill.commercial_emails) ? prefill.commercial_emails : []),
      prefill.commercial_email,
    ]
      .map((email) => String(email ?? '').trim().toLowerCase())
      .filter(Boolean),
  );
  const needleName = normalizePersonName(prefill.commercial_nom);

  const normalized = list.map((option) => {
    const value = option.value != null ? String(option.value) : String(option.id ?? '');
    const label = String(option.label ?? option.nom ?? value);
    const email = String(option.email ?? '').trim().toLowerCase();
    return { value, label, email, raw: option };
  });

  if (idStr) {
    const byId = normalized.find((option) => option.value === idStr);
    if (byId) return byId;
  }

  if (emails.size > 0) {
    const byEmail = normalized.find((option) => option.email && emails.has(option.email));
    if (byEmail) return byEmail;
  }

  if (needleName) {
    const byExactName = normalized.find((option) => normalizePersonName(option.label) === needleName);
    if (byExactName) return byExactName;

    const needleFirst = firstToken(needleName);
    const byPartialName = normalized.find((option) => {
      const candidate = normalizePersonName(option.label);
      if (!candidate) return false;
      return (
        candidate.startsWith(needleName)
        || needleName.startsWith(candidate)
        || firstToken(candidate) === needleFirst
      );
    });
    if (byPartialName) return byPartialName;
  }

  return null;
}

function normalizePersonName(name) {
  const value = String(name ?? '').trim().toLowerCase();
  if (!value) return '';
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function firstToken(normalizedName) {
  return String(normalizedName ?? '').split(/\s+/)[0] || '';
}
